/**
 * Patient name validation — canonical client-side implementation.
 *
 * Mirrors the backend rule in app/core/validators.py :: validate_patient_name().
 *
 * Allows:
 *   - Any Unicode letter (\p{L} — covers Latin, Cyrillic, Greek, Turkish, Arabic, …)
 *   - Spaces
 *   - Hyphens (-)
 *   - Apostrophes:
 *       ' U+0027 straight apostrophe
 *       ' U+2018 left curly  (Mac/iOS smart-quotes left)
 *       ' U+2019 right curly (Mac/iOS smart-quotes right — the common one)
 *
 * Rejects:
 *   - Digits
 *   - Underscores
 *   - Any other punctuation or symbol character
 *   - Values that contain no letters at all (e.g. "--", "  ")
 *   - Empty / whitespace-only strings
 *
 * IMPORTANT — regex construction notes:
 *   1. \p{L} REQUIRES the `u` flag (ES2018+, all modern browsers).
 *   2. The hyphen MUST be at the END of the character class (before `]`)
 *      so it is always treated as a literal character, never a range operator.
 *      Writing \- (escaped hyphen) inside a character class with the `u` flag
 *      is not a valid IdentityEscape per the ECMAScript spec; some parsers
 *      (including @babel/plugin-transform-unicode-property-regex's regjsparser)
 *      reject it, which breaks the Babel transformation and produces the wrong
 *      compiled output for this project's ES5 target.
 *   3. \u2018 and \u2019 are listed explicitly BEFORE the trailing `-`
 *      so they are never mistaken for range endpoints.
 */

// Single source-of-truth pattern. Read the notes above before modifying.
const NAME_CHAR_PATTERN = /^[\p{L}\s'\u2018\u2019-]+$/u;
const NAME_HAS_LETTER   = /\p{L}/u;

// ---------------------------------------------------------------------------
// Quick self-test — runs once at module load time.
// Helps catch build-time regex mangling (e.g. Babel transform bugs) early,
// by surfacing failures as console warnings rather than silent mis-validation.
// ---------------------------------------------------------------------------
/* eslint-disable no-console */
(function selfTest() {
    const pass = [
        "Ilian",
        "Илиян",
        "O'Reilly",          // straight apostrophe U+0027
        "O\u2018Reilly",     // left curly  U+2018
        "O\u2019Reilly",     // right curly U+2019 — the Mac/iOS default
        "Jean-Luc",
        "Mary Jane",
        "\u015Eahin",        // Şahin (Turkish)
    ];
    const fail = [
        "test 2",
        "record 5",
        "123",
        "Anna!!!",
        "John_Smith",
        "--",
    ];
    let ok = true;
    for (const name of pass) {
        if (!NAME_CHAR_PATTERN.test(name.trim()) || !NAME_HAS_LETTER.test(name.trim())) {
            console.warn("[nameValidator] SELF-TEST FAILED — expected PASS for:", JSON.stringify(name));
            ok = false;
        }
    }
    for (const name of fail) {
        if (NAME_CHAR_PATTERN.test(name.trim()) && NAME_HAS_LETTER.test(name.trim())) {
            console.warn("[nameValidator] SELF-TEST FAILED — expected FAIL for:", JSON.stringify(name));
            ok = false;
        }
    }
    if (ok) {
        console.debug("[nameValidator] self-test passed");
    }
})();
/* eslint-enable no-console */

/**
 * Returns true when `value` is a valid patient name.
 * Always trims the input first.
 */
export function isValidPatientName(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (!NAME_HAS_LETTER.test(trimmed)) return false;
    return NAME_CHAR_PATTERN.test(trimmed);
}

/**
 * Ant Design async validator for use in Form.Item `rules`.
 *
 * Empty / whitespace-only values pass through — the `required` rule handles those.
 *
 * Usage:
 *   rules={[{ validator: patientNameValidator(intl.formatMessage({id: 'letters_only_field'})) }]}
 */
export function patientNameValidator(message: string) {
    return (_: unknown, value: string): Promise<void> => {
        if (!value || !value.trim()) return Promise.resolve();
        if (!isValidPatientName(value)) return Promise.reject(new Error(message));
        return Promise.resolve();
    };
}
